import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { API_URL, FOUNDING_CAP } from "@/config";
import SuccessCard from "./SuccessCard";
import NidraMark from "./NidraMark";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^\+[1-9]\d{7,14}$/;
const EASE = [0.16, 1, 0.3, 1];

const GENDER_OPTIONS = [
  { value: "female", label: "Woman" },
  { value: "male", label: "Man" },
  { value: "non-binary", label: "Non-binary" },
  { value: "prefer-not-to-say", label: "Prefer not to say" },
];

const inputCls =
  "w-full rounded-[3px] border-2 border-[#171512]/15 bg-white px-4 py-3.5 font-mono text-sm text-[#171512] placeholder-[#171512]/30 outline-none transition-colors duration-200 focus:border-[#171512]";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.22em] text-[#171512]/50";

export default function WaitlistForm({ taken, cap, sleepRating = null }) {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+91");
  const [founding, setFounding] = useState(false);
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const source = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get("utm_source") || "direct").slice(0, 100);
  }, []);

  const referredBy = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = parseInt(params.get("ref"), 10);
    return Number.isInteger(ref) && ref > 0 ? ref : null;
  }, []);

  const remaining = taken == null ? null : cap - taken;
  const foundingFull = remaining === 0;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    const cleanPhone = phone.replace(/[\s\-()]/g, "");
    if (name.trim().length < 2) {
      setError({ message: "Please enter your name." });
      return;
    }
    if (!gender) {
      setError({ message: "Please select a gender option." });
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError({ message: "Please enter a valid email address." });
      return;
    }
    if (!PHONE_RE.test(cleanPhone)) {
      setError({ message: "Phone must be in E.164 format, for example +919876543210." });
      return;
    }
    if (!consent) {
      setError({ message: "Consent is required to join the waitlist." });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          gender,
          email: email.trim(),
          phone: cleanPhone,
          founding_requested: founding && !foundingFull,
          consent,
          sleep_rating: sleepRating,
          source,
          referred_by: referredBy,
          company,
        }),
      });
      const data = await res.json();
      if (res.status === 409) {
        setError({ code: data.code, message: data.message });
      } else if (!res.ok) {
        setError({
          message: typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again.",
        });
      } else {
        setResult({ ...data, foundingRequested: founding });
      }
    } catch {
      setError({ message: "Network error. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="relative overflow-hidden border-t-2 border-[#171512]/10">
      <NidraMark className="bottom-[-10%] left-[-4%]" />
      <div className="relative mx-auto max-w-6xl px-6 py-28 sm:py-36">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="grid gap-16 lg:grid-cols-2"
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#171512]/50">
              COME IN EARLY.
            </p>
            <h2
              className="mt-6 whitespace-nowrap font-display font-medium leading-[1.15] tracking-tight text-[#171512]"
              style={{ fontSize: "clamp(0.8rem, 3.3vw, 1.28rem)" }}
            >
              DON’T JUST WATCH US BUILD IT.{" "}
              <span className="font-serif italic">HELP US BUILD IT.</span>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-[1.8] text-[#171512]/60 sm:text-base">
              Be the first to know when we launch, and choose whether you want to be part of the
              Founding Community.
            </p>
            {remaining != null && !foundingFull && (
              <p
                data-testid="founding-remaining-text"
                className="mt-8 font-mono text-[11px] tracking-[0.22em] text-[#B9822E]"
              >
                {remaining} OF {FOUNDING_CAP} FOUNDING SEATS LEFT
              </p>
            )}
          </div>

          <div>
            {result ? (
              <SuccessCard result={result} onDismiss={() => setResult(null)} />
            ) : (
              <form
                data-testid="waitlist-form"
                onSubmit={submit}
                noValidate
                className="space-y-6 rounded-[28px] border-2 border-[#F3B9CD] bg-white p-6 shadow-[10px_10px_0_#FDE7EF] sm:p-10"
              >
                <div>
                  <label htmlFor="waitlist-name" className={labelCls}>
                    Name
                  </label>
                  <input
                    id="waitlist-name"
                    data-testid="waitlist-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>

                <div>
                  <span className={labelCls}>Gender</span>
                  <div className="flex flex-wrap gap-2" data-testid="waitlist-gender-group">
                    {GENDER_OPTIONS.map((g) => (
                      <button
                        key={g.value}
                        type="button"
                        data-testid={`waitlist-gender-${g.value}`}
                        onClick={() => setGender(g.value)}
                        className={`rounded-full border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-200 ${
                          gender === g.value
                            ? "border-transparent bg-[#FDE7EF] font-bold text-[#171512]"
                            : "border-[#171512]/15 bg-white text-[#171512]/60 hover:border-[#171512]/40"
                        }`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="waitlist-email" className={labelCls}>
                    Email
                  </label>
                  <input
                    id="waitlist-email"
                    data-testid="waitlist-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label htmlFor="waitlist-phone" className={labelCls}>
                    Phone number
                  </label>
                  <input
                    id="waitlist-phone"
                    data-testid="waitlist-phone-input"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Your phone number"
                    className={inputCls}
                  />
                </div>

                <input
                  data-testid="waitlist-honeypot-input"
                  type="text"
                  name="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />

                <div
                  className={`rounded-xl border-2 p-5 transition-colors duration-200 ${
                    foundingFull
                      ? "border-[#171512]/15 bg-[#F1ECE2] opacity-70"
                      : founding
                        ? "border-[#171512] bg-[#FDE7EF]"
                        : "border-[#171512]/15 bg-white"
                  }`}
                >
                  <p className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-[#171512]">
                    I want to be part of the founding community
                  </p>
                  <label
                    className={`mt-3 flex items-center gap-3 ${foundingFull ? "cursor-not-allowed" : "cursor-pointer"}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      data-testid="waitlist-founding-checkbox"
                      type="checkbox"
                      checked={founding}
                      disabled={foundingFull}
                      onChange={(e) => setFounding(e.target.checked)}
                      className="h-5 w-5 shrink-0 accent-[#171512]"
                    />
                    <span className="text-sm font-semibold text-[#171512]">Yes, I want in.</span>
                  </label>
                  <p
                    data-testid="founding-capacity-note"
                    className="mt-3 text-xs leading-relaxed text-[#171512]/60"
                  >
                    {foundingFull
                      ? "The Founding Community is full. The waitlist stays open."
                      : "The first 200 people who choose this become Nostos Founding Community members."}
                  </p>
                </div>

                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    data-testid="waitlist-consent-checkbox"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 accent-[#171512]"
                  />
                  <span className="text-sm leading-relaxed text-[#171512]/70">
                    I agree to the{" "}
                    <a href="/privacy" className="text-[#B9822E] underline-offset-4 hover:underline">
                      Privacy Policy
                    </a>{" "}
                    and consent to receive waitlist dispatches.
                  </span>
                </label>

                {error && (
                  <p
                    data-testid={error.code ? `waitlist-error-${error.code.toLowerCase().replace("_", "-")}` : "waitlist-error"}
                    className="font-mono text-xs tracking-[0.04em] text-[#C0392B]"
                  >
                    {error.message}
                  </p>
                )}

                {sleepRating != null && (
                  <p
                    data-testid="form-sleep-rating-note"
                    className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#171512]/45"
                  >
                    sleep rating: {sleepRating}/10, saved with your signup
                  </p>
                )}

                <button
                  data-testid="waitlist-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-[#171512] px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-[#FDFBF6] transition-colors duration-200 hover:bg-[#F5C7D4] hover:text-[#171512] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Joining" : "JOIN NOSTOS"}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
